import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../../api/client'

/**
 * Loading and error states, shared by every page.
 *
 * Card skeletons rather than a spinner: the page keeps its shape while it loads, which
 * stops the layout jumping when the content arrives and gives the visitor something to
 * read the structure of. A spinner tells them nothing except that they are waiting.
 */
export function CardSkeletonGrid({ count = 3, height = 320 }: { count?: number; height?: number }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
      }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index}>
          <Skeleton variant="rounded" height={height * 0.55} sx={{ borderRadius: 4, mb: 1.5 }} />
          <Skeleton variant="text" width="70%" height={30} />
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="45%" />
        </Box>
      ))}
    </Box>
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation()

  const offline = error instanceof ApiError && error.status === 0
  const detail = error instanceof ApiError ? error.message : undefined

  return (
    <Alert
      severity={offline ? 'warning' : 'error'}
      sx={{ borderRadius: 3, my: 3 }}
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{offline ? t('common.offline') : t('common.errorTitle')}</AlertTitle>
      {offline ? null : (detail ?? t('common.errorBody'))}
    </Alert>
  )
}

export function EmptyState({ icon, title, body }: { icon: string; title: string; body?: string }) {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 8, textAlign: 'center' }}>
      <Box aria-hidden sx={{ fontSize: '3rem', lineHeight: 1 }}>
        {icon}
      </Box>
      <Typography variant="h5">{title}</Typography>
      {body && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          {body}
        </Typography>
      )}
    </Stack>
  )
}

/** A page-level section with an optional eyebrow, used across the marketing routes. */
export function Section({
  id,
  kicker,
  title,
  subtitle,
  action,
  children,
  sx,
}: {
  /** Anchor target for the chip rail. */
  id?: string
  kicker?: string
  title?: string
  subtitle?: string
  action?: ReactNode
  children: ReactNode
  sx?: object
}) {
  return (
    <Container id={id} component="section" sx={{ py: { xs: 6, md: 9 }, ...sx }}>
      {(kicker || title || subtitle) && (
        <Stack
          direction={{ xs: 'column', sm: action ? 'row' : 'column' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: action ? 'flex-end' : 'flex-start' }}
          spacing={2}
          sx={{ mb: { xs: 3.5, md: 5 } }}
        >
          <Box sx={{ maxWidth: 720 }}>
            {kicker && (
              <Typography variant="overline" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                {kicker}
              </Typography>
            )}
            {title && (
              <Typography variant="h2" sx={{ mb: subtitle ? 1.5 : 0 }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="subtitle1" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
      )}
      {children}
    </Container>
  )
}
